import type { UserProfile } from '@/types/api';
import { apiRequest, clearAuthenticatedBrowserState, queryClient } from './queryClient';

export const AUTH_CAPABILITY_SCHEMA_VERSION = 'allocator-auth-capability-v1';

interface AuthCapability {
  schemaVersion: typeof AUTH_CAPABILITY_SCHEMA_VERSION;
  oidcReady: boolean;
}

export function parseAuthCapability(value: unknown): AuthCapability | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return null;
  const candidate = value as Record<string, unknown>;
  const keys = Object.keys(candidate).sort();
  if (keys.length !== 2 || keys[0] !== 'oidcReady' || keys[1] !== 'schemaVersion') return null;
  if (candidate.schemaVersion !== AUTH_CAPABILITY_SCHEMA_VERSION) return null;
  if (typeof candidate.oidcReady !== 'boolean') return null;
  return {
    schemaVersion: AUTH_CAPABILITY_SCHEMA_VERSION,
    oidcReady: candidate.oidcReady,
  };
}

export async function allocatorOidcIsReady(signal?: AbortSignal): Promise<boolean> {
  try {
    const response = await fetch('/api/auth/status', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      credentials: 'omit',
      redirect: 'error',
      referrerPolicy: 'no-referrer',
      signal,
    });
    const mediaType = response.headers.get('content-type')?.split(';', 1)[0].trim().toLowerCase();
    if (!response.ok || mediaType !== 'application/json') {
      return false;
    }
    return parseAuthCapability(await response.json())?.oidcReady === true;
  } catch {
    return false;
  }
}

export const logout = async () => {
  try {
    await apiRequest('POST', '/api/auth/logout');
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    clearAuthenticatedBrowserState();
    window.location.reload();
  }
};

export const getCurrentUser = (): UserProfile | null => {
  const userData = queryClient.getQueryData(['/api/auth/user']) as UserProfile | null;
  return userData;
};
